import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {EditRounded, SaveRounded} from "@mui/icons-material";
import {Box, Button, Grid, IconButton, Paper, Stack, Switch, Tab, Tabs, TextField, useTheme} from "@mui/material";
import {TimePicker} from "@mui/x-date-pickers";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import {useLocation, useNavigate} from "react-router-dom";
import {useForm, FormProvider} from "react-hook-form";
import {useRecoilValue} from "recoil";
import {useTranslation} from "react-i18next";
import {AgGridReact} from "ag-grid-react";
import {CellPosition, CellValueChangedEvent, ColDef, ICellRendererParams, TabToNextCellParams} from "ag-grid-community";
import {useMutation} from "@tanstack/react-query";
import dayjs from "dayjs";
import ContentBody from "../../../common/content-body/ContentBody";
import SectionHeader from "../../../../../util/component/SectionHeader";
import {generalInfoState} from "../../../search-dms/state";
import {createGeneralTimeframe, createSpecificTimeframe, findGeneralTimeframe, findSpecificTimeframe} from "../../dmsDetail.actions";
import {GENERAL_TIMEFRAME_EMPTY} from "../data";
import {IGeneralTimeframe, ISpecificTimeframe, ITimeframeTabs} from "../../models";
import FormDatePicker from "../../../common/form-fields-wrappers/FormDatePicker";
import {SET_SNACKBAR} from "../../../common/utils";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {NetworkScope} from "../../../types";
import {useHasScope} from "../../../../../config/globalConfig";
import CustomLoader from "../../../../../util/component/CustomLoader";

const TimeframePanel = () => {
    const { i18n } = useTranslation();
    const {t} = useTranslation(["network", "translation"]);
    const theme = useTheme();
    const navigate = useNavigate();
    const location: any = useLocation();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const hasScope = useHasScope;
    const [tab, setTab] = useState<ITimeframeTabs>(ITimeframeTabs.GENERAL_TIMEFRAME);
    const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    }, []);
    const generalInfo = useRecoilValue(generalInfoState);
    const [isDisabledSpecific, setIsDisabledSpecific] = useState(true);
    const [isDisabledGeneral, setIsDisabledGeneral] = useState(true);
    const [isClose, setIsClose] = useState(false);
    const [generalTimeframe, setGeneralTimeframe] = useState<IGeneralTimeframe[]>([]);
    const [specificTimeframe, setSpecificTimeframe] = useState<ISpecificTimeframe[]>([]);
    const [dmsTimezone, setDmsTimezone] = useState<string>("Europe/Rome");
    const timeframePanelSpecificForm = useForm({
        defaultValues: {
            id: generalInfo.id,
            from: null,
            to: null
        }
    })

    const gridRefGeneral = useRef<AgGridReact>(null);
    const gridRefSpecific = useRef<AgGridReact>(null);
    const columnGeneralDefs = useMemo<ColDef[]>(() => [
        {
            field: 'dayOfWeek',
            headerName: t(`dmsDetail.timeframe.table.dayOfWeek`),
            valueFormatter: (params) => {
                return t(`dmsDetail.timeframe.dayOfWeek.${params.value}`)
            }
        },
        {
            field: 'isOpen',
            headerName: t("dmsDetail.timeframe.table.closeOpen"),
            cellRenderer: (p: any) => <>
                <Switch disabled={isDisabledGeneral}
                        onClick={(e: any) => {
                            handleChangeIsOpen(e, p)
                        }}
                        checked={p.value}/>
            </>,
        },
        {
            field: 'opening',
            headerName: t("dmsDetail.timeframe.table.opening"),
            cellRenderer: (p: any) => <> <TimePicker disabled={isDisabledGeneral} onChange={(e: any) => {
                handleChangeFrom(e, p)
            }} renderInput={(params) => <TextField {...params}  />} value={p.value}/></>,
        },
        {
            field: 'breakFrom',
            headerName: t("dmsDetail.timeframe.table.breakFrom"),
            cellRenderer: (p: any) => <> <TimePicker disabled={isDisabledGeneral} onChange={(e: any) => {
                handleChangeFrom(e, p)
            }} renderInput={(params) => <TextField {...params} />} value={p.value}/></>
        },
        {
            field: 'breakTo',
            headerName: t("dmsDetail.timeframe.table.breakTo"),
            cellRenderer: (p: any) => <> <TimePicker disabled={isDisabledGeneral} onChange={(e: any) => {
                handleChangeFrom(e, p)
            }} renderInput={(params) => <TextField {...params} />} value={p.value}/></>
        },
        {
            field: 'closing',
            headerName: t("dmsDetail.timeframe.table.closing"),
            cellRenderer: (p: any) => <> <TimePicker disabled={isDisabledGeneral} onChange={(e: any) => {
                handleChangeFrom(e, p)
            }} renderInput={(params) => <TextField {...params} />} value={p.value}/></>
        },
    ], [isDisabledGeneral, i18n.language]);

    const columnSpecificDefs = useMemo<ColDef[]>(() => [
        {
            field: 'dayOfWeek',
            headerName: t(`dmsDetail.timeframe.table.dayOfWeek`),
            valueFormatter: (params) => {
                return t(`dmsDetail.timeframe.dayOfWeek.${params.value}`)
            }
        },
        // {field: 'dayOfMonth', headerName: "Date", valueFormatter: (params) => {
        //         return dayjs(params.value).format('DD-MM-YYYY');}},
        {field: 'dayOfMonth',
            headerName: t("dmsDetail.timeframe.table.date")
        },
        {
            field: 'isOpen',
            headerName: t("dmsDetail.timeframe.table.closeOpen"),
            cellRenderer: (params: ICellRendererParams) => <>
                <Switch disabled={isDisabledSpecific}
                        onClick={(e: any) => {
                            handleChangeIsOpen(e, params)
                        }}
                        checked={params.value}/>
            </>,
        },
        {
            field: 'opening',
            headerName: t("dmsDetail.timeframe.table.opening"),
            cellRenderer: (p: any) => <> <TimePicker disabled={isDisabledSpecific} onChange={(e: any) => {
                handleChangeFrom(e, p)
            }} renderInput={(params) => <TextField {...params} />} value={p.value}/></>
        },
        {
            field: 'breakFrom',
            headerName: t("dmsDetail.timeframe.table.breakFrom"),
            cellRenderer: (p: any) => <> <TimePicker disabled={isDisabledSpecific} onChange={(e: any) => {
                handleChangeFrom(e, p)
            }} renderInput={(params) => <TextField {...params} />} value={p.value}/></>
        },
        {
            field: 'breakTo',
            headerName: t("dmsDetail.timeframe.table.breakTo"),
            cellRenderer: (p: any) => <> <TimePicker disabled={isDisabledSpecific} onChange={(e: any) => {
                handleChangeFrom(e, p)
            }} renderInput={(params) => <TextField {...params} />} value={p.value}/></>
        },
        {
            field: 'closing',
            headerName: t("dmsDetail.timeframe.table.closing"),
            cellRenderer: (p: any) => <> <TimePicker disabled={isDisabledSpecific} onChange={(e: any) => {
                handleChangeFrom(e, p)
            }} renderInput={(params) => <TextField {...params} />} value={p.value}/></>
        },
    ], [isDisabledSpecific]);

    const handleChangeFrom = (newValue: any | null, secondValue: any) => {
        secondValue.setValue(newValue);
    };

    const handleChangeIsOpen = (newValue: any | null, secondValue: any) => {
        secondValue.setValue(newValue.target.checked);
        setIsClose(newValue.target.checked);
    };

    const defaultColDef = useMemo(() => {
        return {
            lockPinned: true,
            suppressSizeToFit: false,
        }
    }, []);

    const defaultColDefSp = useMemo(() => {
        return {
            lockPinned: true,
            suppressSizeToFit: false,
        }
    }, []);

    const handleBackStep = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        let path = `/mobility-store/dms-detail/${location.state.msId}`;
        navigate(path, {
            state: {
                msId: location.state.msId
            }
        });
    }
    useEffect(() => {
        const reqParams = {
            id: location.state.msId
        };
        findGeneralTimeframe(reqParams)
            .then((response: any) => {
                // Salva il timezone del DMS
                if (response.zoneId) {
                    setDmsTimezone(response.zoneId);
                }
                response.generalTimeFrame.length > 0 ? setGeneralTimeframe(response.generalTimeFrame) : setGeneralTimeframe(GENERAL_TIMEFRAME_EMPTY);
            })
            .catch(() => {
                dispatchGlobalData(SET_SNACKBAR.ERROR);
            })

        findSpecificTimeframe(reqParams)
            .then((response: any) => {
                // Salva il timezone del DMS (backup)
                if (response.timezoneId) {
                    setDmsTimezone(response.timezoneId);
                }
                setSpecificTimeframe(response.specificTimeFrame);
            })
            .catch(() => {
                dispatchGlobalData(SET_SNACKBAR.ERROR);
            })
            .finally(() => {

            })
    }, []);

    const handleEditModeGeneral = () => {
        setIsDisabledGeneral(!isDisabledGeneral);

    }

    const handleEditModeSpecific = () => {
        setIsDisabledSpecific(!isDisabledSpecific);
    }

    const onGeneralCellValueChanged = useCallback((params: CellValueChangedEvent) => {
        let changedData = [params.data];
        setGeneralTimeframe(generalTimeframe.map((x) => changedData[0].dayOfWeek === x.dayOfWeek ? changedData[0] : x));
        gridRefGeneral.current!.api.applyTransaction({update: generalTimeframe.map((x) => changedData[0].dayOfWeek === x.dayOfWeek ? changedData[0] : x)});
    }, [generalTimeframe]);

    const onSpecificCellValueChanged = useCallback((params: CellValueChangedEvent) => {
        let changedData = [params.data];
        setSpecificTimeframe(specificTimeframe.map((x) => changedData[0].dayOfMonth === x.dayOfMonth ? changedData[0] : x));
        gridRefSpecific.current!.api.applyTransaction({update: specificTimeframe.map((x) => changedData[0].dayOfMonth === x.dayOfMonth ? changedData[0] : x)});
    }, [specificTimeframe]);

    const createGeneralTimeframeMutation = useMutation(
        createGeneralTimeframe,
        {
            onSuccess: (response: any) => {
                setIsDisabledGeneral(true);
                setGeneralTimeframe(response.generalTimeFrame);
            },
            onError: (error) => {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        });

    const createSpecificTimeframeMutation = useMutation(
        createSpecificTimeframe,
        {
            onSuccess: (response: any) => {
                setIsDisabledSpecific(true);
                setSpecificTimeframe(response.specificTimeFrame);
            },
            onError: (error) => {
                dispatchGlobalData({
                    type: 'SET_SNACKBAR',
                    payload: {
                        snackbar: {
                            message: t('translation:error.tryLater'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        });

    const showStatusGeneral = () => {
        const isValid = checkHours(generalTimeframe);
        if (isValid) {
            const reqParams: any = {
                id: location.state.msId,
                generalTimeFrame: generalTimeframe,
                dmsTimezone: dmsTimezone  // Passa il timezone per la conversione
            }

            createGeneralTimeframeMutation.mutate(reqParams);
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('dmsDetail.timeframe.message.error.invalidState'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    }

    const checkHours = (array: any) => {
        return array.every((obj: any) => {
            if (obj.isOpen === true) {
                return obj.opening !== null && obj.closing !== null;
            } else if (obj.isOpen === false) {
                return obj.opening === null && obj.breakFrom === null &&
                    obj.breakTo === null && obj.closing === null;
            }
            // se il valore di isOpen non è nè true nè false, la funzione restituirà false
            return false;
        });
    }

    const showStatusSpecific = () => {
        const isValid = checkHours(specificTimeframe);
        if (isValid) {
            const reqParams: any = {
                id: location.state.msId,
                specificTimeFrame: specificTimeframe,
                dmsTimezone: dmsTimezone  // Passa il timezone per la conversione
            }

            createSpecificTimeframeMutation.mutate(reqParams);
        } else {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('dmsDetail.timeframe.message.error.invalidState'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }

    }

    const onSubmit = (data: any) => {
        createDaysArray(data.from, data.to);
    }

    function createDaysArray(startDate: any, endDate: any) {
        const daysArray = [];
        let currentDate = dayjs(startDate);
        const end = dayjs(endDate).add(1, 'day');
        while (currentDate.isBefore(end)) {
            daysArray.push(
                {
                    dayOfWeek: t(`dmsDetail.timeframe.dayOfWeek.${currentDate.format('dddd')}`),
                    dayOfMonth: currentDate.format('DD-MM-YYYY'),
                    isOpen: false,
                    opening: null,
                    breakFrom: null,
                    breakTo: null,
                    closing: null
                });
            currentDate = currentDate.add(1, 'day');
        }

        setSpecificTimeframe(daysArray);
        return daysArray;
    }

    function tabToNextCell(params: TabToNextCellParams): CellPosition | null {
        const previousCell = params.previousCellPosition;
        const renderedRowCount = params.api!.getDisplayedRowCount();
        const lastRowIndex = previousCell.rowIndex;

        let nextRowIndex = params.backwards ? lastRowIndex - 1 : lastRowIndex + 1;

        if (nextRowIndex < 0) {
            nextRowIndex = -1;
        }

        if (nextRowIndex >= renderedRowCount) {
            nextRowIndex = renderedRowCount - 1;
        }

        const result = {
            rowIndex: nextRowIndex,
            column: previousCell.column,
            rowPinned: previousCell.rowPinned,
        };

        return result;
    }

    const suppressKeyboardEvent = ({event, api}: any) => {
        const { key, shiftKey } = event;
        if (key === "Tab") {
            const activeElement = document.activeElement as HTMLElement;
            // @ts-ignore
            if (activeElement?.role === "gridcell") {
                // @ts-ignore
                activeElement?.firstChild?.focus();
                return true;
            }
        }
        return false; // Comportamento predefinito
    };

    function tabToNextCellSpecific(params: TabToNextCellParams): CellPosition | null {
        const previousCell = params.previousCellPosition;
        const renderedRowCount = params.api!.getDisplayedRowCount();
        const lastRowIndex = previousCell.rowIndex;

        let nextRowIndex = params.backwards ? lastRowIndex - 1 : lastRowIndex + 1;

        if (nextRowIndex < 0) {
            nextRowIndex = -1;
        }

        if (nextRowIndex >= renderedRowCount) {
            nextRowIndex = renderedRowCount - 1;
        }

        const result = {
            rowIndex: nextRowIndex,
            column: previousCell.column,
            rowPinned: previousCell.rowPinned,
        };

        return result;
    }

    const suppressKeyboardEventSpecific = ({event, api}: any) => {
        const { key, shiftKey } = event;
        if (key === "Tab") {
            const activeElement = document.activeElement as HTMLElement;
            // @ts-ignore
            if (activeElement?.role === "gridcell") {
                // @ts-ignore
                activeElement?.firstChild?.focus();
                return true;
            }
        }
        return false; // Comportamento predefinito
    };

    return (
        <>
            <SectionHeader title={t("dmsDetail.timeframe.header.title")}/>
            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                        <Tabs value={tab} onChange={handleChangeTab}>
                            <Tab label={t("dmsDetail.timeframe.tab.generic")}/>
                            <Tab label={t("dmsDetail.timeframe.tab.specific")}/>
                        </Tabs>
                    </Box>
                    <Box py={3}>
                        {tab === ITimeframeTabs.GENERAL_TIMEFRAME &&
                            <>
                                <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                    { hasScope(NetworkScope.MsMng) && (isDisabledGeneral ?
                                        <IconButton color={"accent"} onClick={handleEditModeGeneral}>
                                            <EditRounded/>
                                        </IconButton> :
                                        <IconButton color={"accent"} onClick={showStatusGeneral}>
                                            <SaveRounded/>
                                        </IconButton>
                                    )}
                                </Stack>
                                <AgGridCustomTable
                                    gridRef={gridRefGeneral}
                                    rowData={generalTimeframe}
                                    pagination={false}
                                    columnDefs={columnGeneralDefs}
                                    onCellValueChanged={onGeneralCellValueChanged}
                                    gridOptions={
                                        {
                                            tabToNextCell: tabToNextCell,
                                            suppressKeyboardEvent: suppressKeyboardEvent,
                                            defaultColDef: defaultColDef,
                                            onGridReady: (event) => {
                                                event.api.sizeColumnsToFit()
                                            }
                                        }
                                    }
                                />
                            </>
                        }
                        {tab === ITimeframeTabs.SPECIFIC_TIMEFRAME &&
                            <>
                                <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                    {hasScope(NetworkScope.MsMng) && (isDisabledSpecific ?
                                        <IconButton color={"accent"} onClick={handleEditModeSpecific}>
                                            <EditRounded/>
                                        </IconButton> :
                                        <IconButton color={"accent"} onClick={showStatusSpecific}>
                                            <SaveRounded/>
                                        </IconButton>
                                    )}
                                </Stack>
                                <FormProvider {...timeframePanelSpecificForm}>
                                    <Grid container direction="row" alignItems="start">
                                        <Grid container item direction="row" xs={12} columnSpacing={2}>
                                            <Grid item xs={3}>
                                                <FormDatePicker
                                                    minDate={dayjs().add(3, 'day')}
                                                    disabled={isDisabledSpecific}
                                                    control={timeframePanelSpecificForm.control}
                                                    name="from"
                                                    label={t('dmsDetail.timeframe.form.label.from')}
                                                />
                                            </Grid>

                                            <Grid item xs={3}>
                                                <FormDatePicker
                                                    minDate={dayjs().add(3, 'day')}
                                                    disabled={isDisabledSpecific}
                                                    control={timeframePanelSpecificForm.control}
                                                    name="to"
                                                    label={t('dmsDetail.timeframe.form.label.to')}
                                                />
                                            </Grid>
                                            {!isDisabledSpecific &&
                                                <Grid item xs={3}
                                                      sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                                    <Button variant="contained" onClick={timeframePanelSpecificForm.handleSubmit(onSubmit)}>
                                                        {t('dmsDetail.timeframe.button.label.apply')}
                                                    </Button>
                                                </Grid>
                                            }
                                        </Grid>
                                    </Grid>
                                </FormProvider>
                                <AgGridCustomTable
                                    gridRef={gridRefSpecific}
                                    rowData={specificTimeframe}
                                    pagination={false}
                                    columnDefs={columnSpecificDefs}
                                    onCellValueChanged={onSpecificCellValueChanged}
                                    gridOptions={
                                        {
                                            tabToNextCell: tabToNextCellSpecific,
                                            suppressKeyboardEvent: suppressKeyboardEventSpecific,
                                            defaultColDef: defaultColDefSp,
                                            onGridReady: (event) => {
                                                event.api.sizeColumnsToFit()
                                            }
                                        }
                                    }
                                />
                            </>
                        }
                    </Box>
                </Paper>
                <Box
                    sx={{display: 'flex', justifyContent: 'space-between', mt: theme.spacing(2), mb: theme.spacing(2)}}>
                    <Button variant={'text'} color={"accent"} onClick={handleBackStep}>
                        {t('dmsDetail.timeframe.button.label.back')}
                    </Button>
                </Box>
            </ContentBody>

            <CustomLoader loading={createGeneralTimeframeMutation.isLoading || createSpecificTimeframeMutation.isLoading} />
        </>
    )
}

export default TimeframePanel;